import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/CurrentUserProvider';
import { DetailPageSkeleton } from '../../components/DetailPageSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { DriverDetailsForm } from './DriverDetailsForm';
import { DriverDocuments } from './DriverDocuments';
import { DriverHistoryTab } from './DriverHistoryTab';
import { DriverStatusControl } from './DriverStatusControl';
import { useDriverDetail } from './use-driver-detail';

/**
 * A driver's own page: fields, documents and the not-yet-available placeholders on one tab, history
 * on the other. Personal data (national ID, date of birth) is behind `driver.view` itself, the
 * access-restriction CLAUDE.md → Data protection asks for — the list screen is what excludes it.
 */
export function DriverDetailPage() {
  const { id = '' } = useParams();
  const { state, updateError, saving, update, changeStatus, uploadDocument } = useDriverDetail(id);
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('driver.manage');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading driver" />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Driver not found" description="This driver doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this driver" description="Something went wrong loading this driver. Try reloading the page." />;
  }

  const { driver } = state;

  return (
    <>
      <PageHeader
        eyebrow="Assets"
        title={driver.name}
        description={driver.phone}
        actions={<DriverStatusControl driver={driver} canManage={canManage} onChangeStatus={changeStatus} />}
      />
      <Tabs
        items={[
          {
            key: 'details',
            label: 'Details',
            children: (
              <div className="flex flex-col gap-8">
                <DriverDetailsForm driver={driver} canManage={canManage} error={updateError} saving={saving} onSave={update} />
                <DriverDocuments driverId={driver.id} documents={driver.documents} canManage={canManage} onUpload={uploadDocument} />
                <DriverPlaceholders />
              </div>
            ),
          },
          { key: 'history', label: 'History', children: <DriverHistoryTab driverId={driver.id} /> },
        ]}
      />
    </>
  );
}

/** Advance balance (M6) and behaviour score (M5) aren't built yet — shown as unavailable rather than a guessed shape those milestones might not match. */
function DriverPlaceholders() {
  return (
    <section className="grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-line bg-surface px-4 py-3">
        <p className="text-sm font-medium text-fg">Advance balance</p>
        <p className="text-sm text-muted">Not yet available</p>
      </div>
      <div className="rounded-lg border border-line bg-surface px-4 py-3">
        <p className="text-sm font-medium text-fg">Behaviour score</p>
        <p className="text-sm text-muted">Not yet available</p>
      </div>
    </section>
  );
}
